import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { contactApi, contactQueryKeys } from "service/crm/api/contact";
import {
  ContactLifecycleStageApi,
  GetContactsParams,
} from "types/crm/contacts";
import { StageCounts } from "types/crm/pagination";

export const useContactsQuery = (
  params: Omit<GetContactsParams, "page">,
  options?: { enabled?: boolean },
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: contactQueryKeys.list(params),
    queryFn: ({ pageParam }) =>
      contactApi.getContacts({ ...params, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });

  // `?? []` per page: flatMap keeps a non-array return as an element, so a page
  // without `content` would put `undefined` into the list and break the table.
  // const contacts = data?.pages.flatMap((p) => p.content) ?? [];
  const contacts = data?.pages.flatMap((p) => p?.content ?? []) ?? [];
  const totalElements = data?.pages[0]?.totalElements ?? 0;
  const stageCounts: StageCounts<ContactLifecycleStageApi> =
    data?.pages[0]?.counts ?? {};

  return {
    contacts,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements,
    stageCounts,
  };
};